import React from "react";
import FilterItemDndList from "./FilterItemDndList";
import MDBox from "components/Basics/MDBox";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import MDTypography from "components/Basics/MDTypography";
import { t } from "i18next";

/**
 * FiltersDndList component
 * This component renders a list of filters that can be reordered via drag-and-drop.
 * It allows users to add, delete, link, and edit filters.
 * @param {Object} props - The properties for the FiltersDndList component.
 * @param {Array} props.items - The list of filter items to display.
 * @param {Function} props.onChange - Callback function to handle changes in the filter list.
 * @param {Object} props.dictionary - The dictionary object containing filter definitions.
 * @param {Function} props.deleteItem - Function to delete a filter item.
 * @param {Function} props.linkItem - Function to link a filter item.
 * @param {Function} props.removeLink - Function to remove a link from a filter item.
 * @param {Function} props.onEdit - Function to handle editing a filter item.
 * @returns {JSX.Element} The rendered FiltersDndList component.
 */
const FiltersDndList = ({
	items,
	onChange,
	dictionary = {},
	deleteItem = () => {},
	linkItem = () => {},
	removeLink = () => {},
	onEdit = () => {}
}) => {
	const moveItem = (fromIndex, toIndex) => {
		const newItems = [...items];
		const [movedItem] = newItems.splice(fromIndex, 1);
		newItems.splice(toIndex, 0, movedItem);
		onChange(newItems);
	};

	const handleDragEnd = result => {
		if (!result.destination) {
			return;
		}

		const sourceIndex = result.source.index;
		const destinationIndex = result.destination.index;

		if (sourceIndex !== destinationIndex) {
			moveItem(sourceIndex, destinationIndex);
		}
	};

	return (
		<DragDropContext onDragEnd={handleDragEnd}>
			<Droppable droppableId="columns" direction="horizontal">
				{(provided, snapshot) => (
					<MDBox
						{...provided.droppableProps}
						ref={provided.innerRef}
						display="flex"
						flexDirection="row"
						flexWrap="wrap"
						gap={1}
						p={1}
						border="1px solid #e0e0e0"
						borderRadius="8px"
						minHeight="60px"
						sx={{
							backgroundColor: snapshot.isDraggingOver ? "#f5f5f5" : "transparent",
							transition: "background-color 0.2s ease"
						}}
					>
						{items.map((item, index) => (
							<Draggable key={item.path} draggableId={item.path} index={index}>
								{(provided, snapshot) => (
									<MDBox
										ref={provided.innerRef}
										{...provided.draggableProps}
										{...provided.dragHandleProps}
										display="flex"
										alignItems="center"
										bgcolor={snapshot.isDragging ? "primary.main" : "grey.100"}
										color={snapshot.isDragging ? "white" : "inherit"}
										borderRadius="4px"
										p={1}
										sx={{
											border: "1px solid #ccc",
											minWidth: "120px",
											cursor: "grab",
											"&:active": {
												cursor: "grabbing"
											},
											boxShadow: snapshot.isDragging ? "0px 4px 8px rgba(0,0,0,0.2)" : "none",
											transform: snapshot.isDragging ? "rotate(5deg)" : "none",
											transition: "all 0.2s ease"
										}}
									>
										<FilterItemDndList
											item={item}
											index={index}
											dictionary={dictionary}
											deleteItem={deleteItem}
											linkItem={linkItem}
											removeLink={removeLink}
											onEdit={onEdit}
										/>
									</MDBox>
								)}
							</Draggable>
						))}
						{provided.placeholder}

						{items.length === 0 && (
							<MDBox
								display="flex"
								alignItems="center"
								justifyContent="center"
								width="100%"
								color="text.secondary"
							>
								<MDTypography variant="body2">{t("FILTERS.noFilterDefined")}</MDTypography>
							</MDBox>
						)}
					</MDBox>
				)}
			</Droppable>
		</DragDropContext>
	);
};

export default FiltersDndList;
